import { useState, useEffect } from 'react';
import { shopify } from '../lib/shopifyClient';

/**
 * Hook to fetch product data directly from Shopify
 * @param {string} variantId - The variant ID (can be numeric or gid://)
 * @returns {object} { variant, loading, error }
 */
export function useShopifyVariant(variantId) {
  const [variant, setVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!variantId) {
      setLoading(false);
      return;
    }

    const fetchVariant = async () => {
      try {
        setLoading(true);
        setError(null);

        // Ensure we have a proper gid
        const numericId = variantId.startsWith('gid://') 
          ? variantId.split('/').pop() 
          : variantId;
        const gid = `gid://shopify/ProductVariant/${numericId}`;
        const encodedId = window.btoa(gid);

        const query = `
          query getVariant($id: ID!) {
            node(id: $id) {
              ... on ProductVariant {
                id
                title
                sku
                availableForSale
                quantityAvailable
                price {
                  amount
                  currencyCode
                }
                compareAtPrice {
                  amount
                  currencyCode
                }
                product {
                  id
                  title
                  handle
                  availableForSale
                  sellingPlanGroups(first: 1) {
                    edges {
                      node {
                        id
                        name
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        const response = await shopify.request(query, { id: encodedId });
        
        if (response?.node) {
          const shopifyVariant = response.node;
          
          // Transform to match expected structure
          setVariant({
            id: shopifyVariant.id,
            variantId: numericId,
            title: shopifyVariant.title,
            sku: shopifyVariant.sku,
            price: parseFloat(shopifyVariant.price.amount),
            compareAtPrice: shopifyVariant.compareAtPrice ? parseFloat(shopifyVariant.compareAtPrice.amount) : null,
            availableForSale: shopifyVariant.availableForSale,
            inventory_quantity: shopifyVariant.quantityAvailable || 0,
            hasSubscription: shopifyVariant.product.sellingPlanGroups.edges.length > 0,
            product: {
              id: shopifyVariant.product.id,
              title: shopifyVariant.product.title,
              handle: shopifyVariant.product.handle,
              availableForSale: shopifyVariant.product.availableForSale
            }
          });
        }
      } catch (err) {
        console.error('Error fetching Shopify variant:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchVariant();
  }, [variantId]);

  return { variant, loading, error };
}

/**
 * Hook to fetch all variants for a product
 * @param {string} productHandle - The product handle/slug
 * @returns {object} { product, variants, loading, error }
 */
export function useShopifyProduct(productHandle) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!productHandle) {
      setLoading(false);
      return;
    }

    const fetchProduct = async () => {
      try {
        setLoading(true);
        setError(null);

        const query = `
          query getProduct($handle: String!) {
            productByHandle(handle: $handle) {
              id
              title
              handle
              availableForSale
              sellingPlanGroups(first: 1) {
                edges {
                  node {
                    id
                    name
                  }
                }
              }
              variants(first: 50) {
                edges {
                  node {
                    id
                    title
                    sku
                    availableForSale
                    quantityAvailable
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                  }
                }
              }
            }
          }
        `;

        const response = await shopify.request(query, { handle: productHandle });
        
        if (response?.productByHandle) {
          const shopifyProduct = response.productByHandle;
          
          // Transform to match expected structure
          const transformedVariants = shopifyProduct.variants.edges.map(({ node }) => ({
            id: node.id,
            variantId: node.id.split('/').pop(),
            title: node.title,
            sku: node.sku,
            price: parseFloat(node.price.amount),
            compareAtPrice: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount) : null,
            availableForSale: node.availableForSale,
            inventory_quantity: node.quantityAvailable || 0,
            hasSubscription: shopifyProduct.sellingPlanGroups.edges.length > 0
          }));

          setProduct({
            id: shopifyProduct.id,
            title: shopifyProduct.title,
            handle: shopifyProduct.handle,
            availableForSale: shopifyProduct.availableForSale,
            hasSubscription: shopifyProduct.sellingPlanGroups.edges.length > 0,
            variants: transformedVariants
          });
        }
      } catch (err) {
        console.error('Error fetching Shopify product:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productHandle]);

  return { product, loading, error };
}